import { makeAutoObservable } from 'mobx'
import { PopupRequest, PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

import { config } from '../Config';
import { errorStore } from './ErrorStore';

const msalConfig = {
	auth: {
		...config.authentication,
		redirectUri: `${window.location.protocol}//${window.location.host}`,
	},
	cache: {
		cacheLocation: 'localStorage',
		storeAuthStateInCookie: true,
	}
};

export const msalInstance = new PublicClientApplication(msalConfig);

const loginRequest: PopupRequest = { scopes: config.scopes };

class AuthStore {
	isAuthenticated = false;

	constructor() {
		// userName is a getter, so we don't need to cache it
		makeAutoObservable(this, { userName: false })
	}

	private async checkAccount() {
		const [account] = msalInstance.getAllAccounts();

		if (account) {
			try {
				await msalInstance.acquireTokenSilent({
					scopes: config.scopes,
					account,
				});
			} catch (error) {
				if (error instanceof InteractionRequiredAuthError) {
					// Session is missing or expired, require user interaction
					await msalInstance.loginPopup(loginRequest);
				} else {
					this.setError(error);
				}
			}
		}
	}

	async initialize() {
		await msalInstance.initialize();
		await this.checkAccount();
		this.setIsAuthenticated(!!this.userName);
	}

	async login() {
		try {
			const [account] = msalInstance.getAllAccounts();
			if (account) {
				try {
					// Try to get the token silently
					await msalInstance.acquireTokenSilent({ ...loginRequest, account });
				} catch (err) {
					// If unable to get the token silently, login again
					await msalInstance.loginPopup(loginRequest);
				}
			} else {
				await msalInstance.loginPopup(loginRequest);
			}

			// Only set authenticated after successful token acquisition
			this.setIsAuthenticated(true);
		} catch (err) {
			this.setIsAuthenticated(false);
			this.setError(err);
		}
	}

	async getAccessToken() {
		const [account] = msalInstance.getAllAccounts();
		if (!account) {
			const { userStore } = await import('./UserStore');
			userStore.clearCurrentUser();
			this.setIsAuthenticated(false);
			return undefined;
		}

		try {
			const token = await msalInstance.acquireTokenSilent({
				scopes: config.scopes,
				account,
			});

			return `Bearer ${token.accessToken}`;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				try {
					// fallback to interaction when silent call fails
					const token = await msalInstance.acquireTokenPopup({
						scopes: config.scopes,
						account,
					});

					return `Bearer ${token.accessToken}`;
				} catch (err) {
					this.setError(err);
					throw err;
				}
			} else {
				this.setError(error);
				throw error;
			}
		}
	}

	async logout() {
		await msalInstance.logoutPopup();
		this.setIsAuthenticated(false);
		const { userStore } = await import('./UserStore');
		userStore.clearCurrentUser();
		this.setError(undefined);
	}

	get userName() {
		const [account] = msalInstance.getAllAccounts();
		return account?.name;
	}

	setIsAuthenticated(value: boolean) {
		this.isAuthenticated = value;
	}

	setError(error: unknown) {
		let errorMessage;
		if (error instanceof Error) {
			const message = error.message.includes(': ')
				? error.message.split(': ').slice(1).join(': ')
				: error.message;
			errorMessage = message;
		} else {
			errorMessage = error as string;
		}
		errorStore.report(errorMessage)
	}
}

export const authStore = new AuthStore()